// Generated by https://quicktype.io
// 1. Select all contents in portdata_xxx.json.
// 2. Now CTRL+SHIFT+P "Paste Json As Types" via QuickType plugin.
// 3. Ensure 'berths: Berth[]' to reinputted to IPortData
// Generated by https://quicktype.io

import { LineProfile } from './FleetManagerVessel';
import { ConstantTensionWinchProfile } from './types';

interface PortLineProfile extends LineProfile {
  tail?: boolean;
  tailLength?: number; // Tail length to use if line type is tail.
}

export interface PortData {
  fenderProfiles: FenderProfile[];
  bollardProfiles: BollardProfile[];
  constantTensionWinchProfiles: ConstantTensionWinchProfile[];
  portLineProfiles: PortLineProfile[];
  constantTensionWinchLineProfiles: LineProfile[];
  buildings: string;
  stockpiles: string;
  accessRoutes: string;
  terminals: Terminal[];
  berths: Berth[];
  utmProjection: string;
  utmZone?: number;
  timeZone: string;
  demoTime?: string;
  dateFormat: string;
  dateTimeFormat: string;
  mslPortDatum: number;
  mslPortDatumUnit: string;
  mlwPortDatum: number;
  mlwPortDatumUnit: string;
  mhwPortDatum: number;
  mhwPortDatumUnit: string;
  datum: string;
  berthPrescreening: BerthPrescreening;
  motionCriteria: MotionCriterion[];
  waveMatrixConditions: WaveMatrixCondition[];
  riverDischargeStations: RiverDischargeStation[];
  defaultMooringArrangements?: DefaultMooringArrangement[];
  allowedMooringArrangements?: DefaultMooringArrangement[];
}

export interface WaveMatrixCondition {
  path: string;
  buoys: WaveMatrixConditionBuoy[];
  measurementDevices: WaveMatrixConditionMeasurementDevice[];
}
export interface WaveMatrixConditionBuoy {
  name: string | null;
  hs: number | null;
  tp: number | null;
  mwd: number | null;
  tide: number | null;
}
export interface WaveMatrixConditionMeasurementDevice {
  name: string | null;
  hsSwell: number | null;
  tpSwell: number | null;
  hsLPW: number | null;
  tpLPW: number | null;
  hsTotal: number | null;
  tpTotal: number | null;
  hsLPW150s: number | null;
  tpLPW150s: number | null;
}

export interface BerthPrescreening {
  units: string;
  thresholds: Thresholds;
}

export interface Thresholds {
  outreachBeyondBeam: Threshold;
  spreaderClearance: Threshold;
  underKeelClearance: Threshold;
}

export interface Threshold {
  warning: number;
  safe: number;
}

export interface BollardProfile {
  bollardType: string;
  bollardProfileId: number;
  failurePct: number;
  warningPct: number;
  horizontalBreakingStrength: number;
  horizontalBreakingStrengthUnit: string;
  verticalBreakingStrength: number;
  verticalBreakingStrengthUnit: string;
  verticalLineAngleMin: number;
  verticalLineAngleMax: number;
  horizontalLineAngleMin: number;
  horizontalLineAngleMax: number;
  quickReleaseHook: boolean;
  quickReleaseHookThreshold: number;
  quickReleaseHookThresholdUnit: string;
  quickReleaseHookNumberOfUnits: number;
  constantTensionWinch: boolean;
  bollardLinesPerProfile: LinesPerBollardHeadLength[];
  maxLines: number;
}

export type LinesPerBollardHead = {
  numberOfBowLines: number;
  numberOfBreastLines: number;
  numberOfSpringLines: number;
};

export interface LinesPerBollardHeadLength extends LinesPerBollardHead {
  minLoa: number;
  maxLoa: number;
}

export function getLinesPerBollardHead(loa: number, bollardProfile: BollardProfile): LinesPerBollardHead {
  for (const lineLength of bollardProfile?.bollardLinesPerProfile || []) {
    if (loa >= lineLength.minLoa && loa <= lineLength.maxLoa) {
      return {
        numberOfBowLines: lineLength.numberOfBowLines,
        numberOfBreastLines: lineLength.numberOfBreastLines,
        numberOfSpringLines: lineLength.numberOfSpringLines,
      };
    }
  }

  // Default return -1 for bollard profiles that have no matching loa range/types:
  return {
    numberOfBowLines: -1,
    numberOfBreastLines: -1,
    numberOfSpringLines: -1,
  };
}

/**
 * Checks a list of default mooring arrangements for a match
 * @param arrangements - Array of default mooring arrangements to check
 * @param loa - The vessel's length overall
 * @param vesselType - The vessel type (optional)
 * @returns The mooring arrangement name, or undefined if no match found
 */
function checkMooringArrangements(
  arrangements: DefaultMooringArrangement[] | undefined,
  loa: number,
  vesselType: number,
): { defaultMooring: string | undefined, additionalMooring: string[] } {
  if (!arrangements || arrangements.length === 0) {
    return { defaultMooring: undefined, additionalMooring: [] };
  }

  // Find rules applying to the given vessel type:
  const filteredArrangements = arrangements.filter((a) => a.vesselType === vesselType || a.vesselType === null || a.vesselType === undefined);

  let defaultMooring: string | undefined = undefined;
  const additionalMooring: string[] = [];

  // First pass: Look for exact vessel type and LOA match
  for (const arrangement of filteredArrangements) {
    if (
      arrangement.vesselType === vesselType &&
      loa >= (arrangement.loaFrom || 0) &&
      loa <= (arrangement.loaTo || Infinity)
    ) {
      if (!defaultMooring) {
        defaultMooring = arrangement.mooringArrangementName;
        additionalMooring.push(arrangement.mooringArrangementName);
      } else if (arrangement.alwaysInclude) {
        additionalMooring.push(arrangement.mooringArrangementName);
      }
    }
  }

  // Second pass: Look for LOA match only (vessel type agnostic)
  for (const arrangement of filteredArrangements) {
    if (
      (arrangement.vesselType === null || arrangement.vesselType === undefined) &&
      loa >= (arrangement.loaFrom || 0) &&
      loa <= (arrangement.loaTo || Infinity)
    ) {
      if (!defaultMooring) {
        defaultMooring = arrangement.mooringArrangementName;
        additionalMooring.push(arrangement.mooringArrangementName);
      } else if (arrangement.alwaysInclude) {
        additionalMooring.push(arrangement.mooringArrangementName);
      }
    }
  }
  
  return { defaultMooring, additionalMooring };
}

/**
 * Gets the default mooring arrangement name based on vessel type and LOA
 * Checks terminal-level configuration first, then falls back to port-level configuration
 * @param loa - The vessel's length overall
 * @param vesselType - The vessel type (optional)
 * @param terminal - The terminal configuration (optional)
 * @param portData - The port data configuration
 * @returns The mooring arrangement name, or undefined if no match found
 */
export function getConfiguredMooringArrangements(
  loa: number,
  vesselType: number | undefined,
  terminal: Terminal | undefined,
  portData: PortData,
): { defaultMooring: string | undefined, additionalMooring: string[] } {
  // First check terminal-level configuration
  const terminalResult = checkMooringArrangements(terminal?.defaultMooringArrangements, loa, vesselType);    

  // Fall back to port-level configuration
  const portResult = checkMooringArrangements(portData.defaultMooringArrangements, loa, vesselType);

  return {
    defaultMooring: terminalResult.defaultMooring || portResult.defaultMooring,
    additionalMooring: [
      ...terminalResult.additionalMooring,
      ...portResult.additionalMooring,
    ]
  }
}

/**
 * Returns the ordered, de-duplicated list of mooring arrangement names a port/terminal
 * restricts selection to, or an empty array when no restriction is configured.
 *
 * When configured, these are the ONLY arrangements that should be shown/available. The
 * first entry is treated as the default arrangement. Entries may optionally be scoped by
 * vessel type and LOA range (like DefaultMooringArrangement); an entry with those fields
 * unset matches every vessel.
 *
 * Terminal-level configuration takes precedence over port-level (mirroring
 * getConfiguredMooringArrangements).
 * @param loa - The vessel's length overall
 * @param vesselType - The vessel type (optional)
 * @param terminal - The terminal configuration (optional)
 * @param portData - The port data configuration
 * @returns Ordered list of allowed mooring arrangement names, or [] if unconfigured
 */
export function getAllowedMooringArrangements(
  loa: number,
  vesselType: number | undefined,
  terminal: Terminal | undefined,
  portData: PortData,
): string[] {
  // Terminal-level configuration takes precedence over port-level:
  const configured = terminal?.allowedMooringArrangements ?? portData.allowedMooringArrangements;

  if (!configured || configured.length === 0) {
    return [];
  }

  const allowed: string[] = [];

  configured.forEach((arrangement) => {
    // vesselType/LOA are optional filters (unset = matches all), so they're inert for Pusan
    // and apply to every vessel. Kept for parity with DefaultMooringArrangement and to let a
    // future port restrict by type/LOA without code changes.
    const vesselTypeMatches =
      arrangement.vesselType === null ||
      arrangement.vesselType === undefined ||
      arrangement.vesselType === vesselType;
    const loaMatches = loa >= (arrangement.loaFrom || 0) && loa <= (arrangement.loaTo || Infinity);

    if (vesselTypeMatches && loaMatches && !allowed.includes(arrangement.mooringArrangementName)) {
      allowed.push(arrangement.mooringArrangementName);
    }
  });

  return allowed;
}

export interface FenderProfile {
  fenderType: string;
  fenderProfileId: number;
  maximumReactionForce: number;
  maximumReactionForceUnit: string;
  maximumDeflection: number;
  maximumDeflectionUnit: string;
  failurePct: number;
  warningPct: number;
  frictionCoefficientType: number;
  frictionCoefficientValue: number;
  frictionCoefficientMin: number;
  frictionCoefficientMax: number;
  frictionCoefficientBinSize: number;
  thickness: number;
  thicknessUnit: string;
  deflectionReactionCurve: Point2D[];
}

export interface Point2D {
  x: number;
  y: number;
}

export interface MotionCriterion {
  name: string;
  min: number;
  max: number;
  peakToPeak: number;
  acceleration: number | undefined;
}

export interface RiverDischargeStation {
  name: string;
  aep: AnnualExceedanceProbability[];
}
export interface AnnualExceedanceProbability {
  year: number;
  peakFlow: number;
}

export interface Terminal {
  berthMarkers: Array<BerthMarker[]>;
  terminalName: string;
  berths: Berth[];
  adjacentTerminals: null;
  markerStartMeter: number;
  sharedBollards: Bollard[] | null;
  wharfHeightAbovePortDatum: number | null;
  mslPortDatum: number | null;
  automaticMooringType?: number | null;
  defaultMooringArrangements?: DefaultMooringArrangement[];
  allowedMooringArrangements?: DefaultMooringArrangement[];
}

export interface Berth {
  berthId: number;
  berthName: string;
  terminalName: string;
  berthType: string;
  adjacentBerths: string[];
  minimumVesselDistance: MinimumVesselDistance[];
  minimumVesselDistanceUnit: string;
  windShelteringCoefficientLandSlidePerCent: number;
  windShelteringLandSideDirectionDegree: number;
  berthPocketLength: number | null;
  berthPocketLengthUnit: string;
  berthPocketWidth: number;
  berthPocketWidthUnit: string;
  airDraftAbovePortDatum: number;
  airDraftAbovePortDatumUnit: string;
  berthPocketDepthBelowPortDatum: number;
  berthPocketDepthBelowPortDatumUnit: string;
  wharfHeightAbovePortDatum: number;
  wharfHeightAbovePortDatumUnit: string;
  maximumVesselDWT: number | null;
  maximumVesselTEU: number | null;
  maximumVesselLOA: number | null;
  maximumVesselLOAUnit: string | null;
  maximumVesselBeam: number | null;
  maximumVesselBeamUnit: string | null;
  berthIsToTheRight: boolean;
  berthMarkerOnOceanSide?: boolean;
  portDock?: boolean;
  berthLine: Array<number[]>;
  berthRuler?: Array<number[]>;
  normalVector: number[];
  shipLoaders: ShipLoader[];
  stormAnchors: StormAnchor[];
  berthStartFromTerminal: number;
  berthRulerOffSet: number;
  berthRulerDirection: string;
  fenders: Fender[];
  bollards: Bollard[];
  forecast: Forecast;
  defaultVesselBerthing: DefaultVesselBerthing[];
  berthPrescreening: BerthPrescreening;
  motionCriteria: MotionCriterion[];
  waveMatrixConditions: WaveMatrixConditionsAtBerth;
}

export interface WaveMatrixConditionsAtBerth {
  fileName: string;
  conditions: WaveMatrixConditionAtBerth[];
}

export interface WaveMatrixConditionAtBerth {
  hsSwell: number | null;
  tpSwell: number | null;
  hsLPW: number | null;
  tpLPW: number | null;
  hsTotal: number | null;
  tpTotal: number | null;
  hsLPW150s: number | null;
  tpLPW150s: number | null;
}

export interface Forecast {
  surfaceElevation: string | null;
  currentSpeed: string | null;
  currentDirection: string | null;
  windDirection: string | null;
  windSpeed: string | null;
  waveHeight: string | null;
  waveDirection: string | null;
  wavePeriod: string | null;
  waves: string | null;
  waveScaleFactor: string | null;
  simulationDurationInSeconds?: number;
  forcingInterpolationTimestepInSeconds?: number;
  simulationTimeStepInSeconds?: number;
  ensembleTimeseries?: {
    direction: string;
    height: string;
    period: string;
    controlHeight: string | undefined;
    controlPeriod: string | undefined;
    controlDirection: string | undefined;
    numberOfModes: number;
    numberOfEnsembles: number;
  };
}

export interface DefaultVesselBerthing {
  loaFrom: number;
  loaTo: number;
  position: number;
}

export interface DefaultMooringArrangement {
  vesselType?: number; // Optional - if not specified, applies to all vessel types
  loaFrom?: number; // Optional - if not specified, no minimum LOA
  loaTo?: number; // Optional - if not specified, no maximum LOA
  mooringArrangementName: string;
  alwaysInclude?: boolean; // Optional - if true, arrangement is always included in available options (if loa/vessel type matches)
}

export interface BerthMarker {
  berthMarkerId: string;
  point: number[];
}

export interface Bollard {
  name: string;
  point: number[];
  zAboveWharfDeck: number;
  zAboveWharfDeckUnit: string;
  profileId: number;
  directionDegreesTrueNorth: number;
  associatedProfile?: BollardProfile;
  status?: string;
  metaData?: string;
  originalIndex?: number;
  numberOfBreastLines: number;
  numberOfBowLines: number;
  numberOfSpringLines: number;
  berthMarker?: number;
}

export interface Fender {
  name: string;
  pointOriginal: number[];
  point: number[];
  zAboveWharfDeck: number;
  zAboveWharfUnit: string;
  profileId: number;
  fenderOffset: number;
  status?: string;
}

export interface MinimumVesselDistance {
  from: number;
  to: number;
  distance: number;
}

export interface ShipLoader {
  shipLoaderId: number;
  shipLoaderName: string;
  numberOfUnits: number;
  spreaderHeightAboveWharf: number;
  spreaderHeightAboveWharfUnit: string;
  outReachOfBoom: number;
  outReachOfBoomUnit: string;
  railToFender: number;
  railToFenderUnit: string;
  maxWindSpeedOperational: number;
  maxWindSpeedOperationalUnit: string;
  widthLegs: number;
}

export interface StormAnchor {
  stormAnchorType: string;
  point: number[];
  zAboveWharffDeck: number;
  zAboveWharffDeckUnit: string;
}
